import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import type { InspectionViewContext } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/inspectionResultSchema'
import type {
  ReportBlock,
  ReportSelectionAction,
  ReportSource,
  ReportView as View,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/reportViewSchema'
import { ExternalLink } from 'lucide-react'
import styled from 'styled-components'

import { ReportAssetView } from './ReportAssetView'
import { ReportChart } from './ReportChart'
import { ReportTable } from './ReportTable'
import { useReportViewerFocus } from './useReportViewerFocus'

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.75rem;
  background: var(--card);
  min-width: 0;
  overflow-wrap: anywhere;
  p {
    margin: 0;
    white-space: pre-wrap;
  }
  ul {
    margin: 0;
    padding-left: 1.25rem;
  }
`
const Metrics = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 1rem;
  margin: 0;
  dt {
    color: var(--muted-foreground);
    font-size: 0.75rem;
  }
  dd {
    margin: 0.5rem 0 0;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }
`
const Callout = styled.aside`
  border-left: 0.125rem solid var(--border);
  padding: 0.5rem 1rem;
  &[data-tone='warning'] {
    border-color: var(--foreground);
  }
`
function SourceContent({
  source,
  block,
  onViewContextChange,
  onSelect,
}: {
  onSelect: (action: ReportSelectionAction) => void
  source: ReportSource
  block: ReportBlock
  onViewContextChange?: (context: InspectionViewContext | null) => void
}) {
  const { t, i18n } = useLingui()
  switch (source.kind) {
    case 'text':
      return source.paragraphs.length ? (
        <>
          {source.paragraphs.map((paragraph, index) => (
            <Text as='p' key={index}>
              {paragraph}
            </Text>
          ))}
        </>
      ) : (
        <Text color='muted'>
          <Trans>Not recorded</Trans>
        </Text>
      )
    case 'callout':
      return (
        <Callout data-tone={source.tone}>
          <Text as='p' variant='caption'>
            {source.text}
          </Text>
        </Callout>
      )
    case 'metrics':
      return (
        <Metrics>
          {source.items.map((item, index) => (
            <div key={index}>
              <dt>{item.label}</dt>
              <dd>
                {item.value === null
                  ? t`Not recorded`
                  : typeof item.value === 'number'
                    ? i18n.number(item.value)
                    : item.value}
                {item.unit ? ` ${item.unit}` : ''}
              </dd>
            </div>
          ))}
        </Metrics>
      )
    case 'table':
      return <ReportTable source={source} block={block} onSelect={onSelect} />
    case 'asset_view':
      return (
        <ReportAssetView
          source={source}
          onViewContextChange={onViewContextChange}
        />
      )
    case 'chart':
      return (
        <ReportChart source={source} chartStyle={block.chart_style ?? 'bar'} />
      )
    case 'link':
      return (
        <Button asChild variant='outline' size='sm'>
          <a href={source.href}>
            {source.title}
            <ExternalLink />
          </a>
        </Button>
      )
  }
}
export function ReportView({
  report,
  onViewContextChange,
}: {
  report: View
  onViewContextChange?: (context: InspectionViewContext | null) => void
}) {
  const sources = new Map(report.sources.map((source) => [source.id, source]))
  const pinned = new Set([...report.required_before, ...report.required_after])
  const blocks: ReportBlock[] = [
    ...report.required_before.map((source_id) => ({ source_id })),
    ...report.layout.blocks.filter((block) => !pinned.has(block.source_id)),
    ...report.required_after.map((source_id) => ({ source_id })),
  ]
  const defaultViewer = blocks.findIndex(
    (block) => sources.get(block.source_id)?.kind === 'asset_view'
  )
  const { selection, focusOn, selectSource, dismissSelection, onContext } =
    useReportViewerFocus(report, `block-${defaultViewer}`, onViewContextChange)
  const activeSource = report.sources.find(
    (source) => source.id === selection?.sourceId
  )
  const selectionKey = `selection-${selection?.revision}`
  return (
    <>
      {blocks.map((block, index) => {
        const source = sources.get(block.source_id)
        if (!source) return null
        return (
          <Section
            key={`${source.id}-${index}`}
            onPointerDownCapture={
              source.kind === 'asset_view'
                ? () => focusOn(`block-${index}`)
                : undefined
            }
            onFocusCapture={
              source.kind === 'asset_view'
                ? () => focusOn(`block-${index}`)
                : undefined
            }>
            {source.kind !== 'link' ? (
              <Text as='h3' variant='body' weight='medium'>
                {block.title || source.title}
              </Text>
            ) : null}
            <SourceContent
              source={source}
              block={block}
              onSelect={selectSource}
              onViewContextChange={onContext(`block-${index}`)}
            />
          </Section>
        )
      })}
      {activeSource?.kind === 'asset_view' ? (
        <Section
          key={`${activeSource.id}-${selection?.revision}`}
          aria-label={activeSource.title}
          onPointerDownCapture={() => focusOn(selectionKey)}
          onFocusCapture={() => focusOn(selectionKey)}>
          <Button variant='outline' size='sm' onClick={dismissSelection}>
            <Trans>Back to report</Trans>
          </Button>
          <Text as='h3' variant='body' weight='medium'>
            {activeSource.title}
          </Text>
          <ReportAssetView
            source={activeSource}
            onViewContextChange={onContext(selectionKey)}
          />
        </Section>
      ) : null}
    </>
  )
}
